import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, TextArea, classes } from './ui.tsx';
import type { SwapItem, SwapPerson, SwapReview, SwapStatus, SwapVisit } from '@shared/shift-swaps.ts';

/** The office's queue read: items plus a before/after review for each one waiting on approval. */
export interface OfficeSwapListing {
  items: SwapItem[];
  reviews: Record<string, SwapReview>;
  nextCursor: string | null;
  waitingOnOwner: number;
  policy: string;
  cutoffHours: number;
}

const CHIP_TONE: Record<SwapStatus, string> = {
  pending_peer: 'border-[color:var(--color-warning)] text-[color:var(--color-warning)]',
  pending_owner: 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]',
  approved:
    'border-[color:var(--color-brand-strong)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
  declined: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
  canceled: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
  expired: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
};

/** Waiting on coworker / Waiting on owner / Approved / Declined / Canceled / Expired. The words carry it; color only helps. */
export function SwapStatusChip({ status }: { status: SwapStatus }) {
  const { t } = useTranslation();
  return <span className={classes('badge', CHIP_TONE[status])}>{t(`shiftSwaps.status.${status}`)}</span>;
}

export function useSwapTime() {
  const { i18n } = useTranslation();
  return (iso: string, timeZone: string) => {
    try {
      return new Date(iso).toLocaleString(i18n.language, { timeZone, dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };
}

export function SwapVisitLine({ label, visit }: { label: string; visit: SwapVisit }) {
  const { t } = useTranslation();
  const time = useSwapTime();
  return (
    <p className="break-words">
      <span className="font-semibold">{label}:</span> {visit.clientName} · {time(visit.startsAt, visit.timezone)}
      {visit.area ? ` · ${t('shiftSwaps.area', { area: visit.area })}` : ''}
    </p>
  );
}

export function personName(person: SwapPerson | null, fallback: string) {
  return person?.name ?? fallback;
}

function Crew({ people }: { people: SwapPerson[] }) {
  const { t } = useTranslation();
  if (!people.length) return <span>{t('shiftSwaps.review.unassigned')}</span>;
  return <span>{people.map((person) => person.name ?? t('shiftSwaps.former')).join(', ')}</span>;
}

/**
 * The approval card: before/after crews for each visit, the conflict check's
 * answer and the same-crew warning, with Approve and Decline. Used on the
 * Swaps tab, the schedule's visit panel and the emailed approve link.
 */
export function SwapApprovalCard({
  item,
  review,
  busy = false,
  error,
  onDecide,
  allowDecline = true,
}: {
  item: SwapItem;
  review: SwapReview | null | undefined;
  busy?: boolean;
  error?: string | null;
  onDecide?: (decision: 'approved' | 'declined', note: string) => void;
  allowDecline?: boolean;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const visits = [item.visit, ...(item.offeredVisit ? [item.offeredVisit] : [])];
  return (
    <article className="work-row space-y-3" aria-label={t('shiftSwaps.review.title')}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-bold">
          {personName(item.requester, t('shiftSwaps.former'))} →{' '}
          {personName(item.acceptedBy ?? item.target, t('shiftSwaps.anyone'))}
        </h3>
        <SwapStatusChip status={item.status} />
        <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
          {item.offeredVisit ? t('shiftSwaps.trade') : t('shiftSwaps.giveAway')}
        </span>
      </div>
      {item.note ? <p className="whitespace-pre-wrap break-words">{item.note}</p> : null}
      {visits.map((visit, index) => {
        const crew = review?.crews.find((entry) => entry.appointmentId === visit.id);
        return (
          <div key={visit.id} className="space-y-1">
            <SwapVisitLine label={index === 0 ? t('shiftSwaps.visit') : t('shiftSwaps.inExchange')} visit={visit} />
            {crew ? (
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 text-sm">
                <dt className="font-semibold">{t('shiftSwaps.review.before')}</dt>
                <dd>
                  <Crew people={crew.before} />
                </dd>
                <dt className="font-semibold">{t('shiftSwaps.review.after')}</dt>
                <dd>
                  <Crew people={crew.after} />
                </dd>
              </dl>
            ) : null}
          </div>
        );
      })}
      {review ? (
        review.conflict ? (
          <Alert tone="error">
            {t('shiftSwaps.review.conflict', { reason: t(`shiftSwaps.problem.${review.conflict}`) })}
          </Alert>
        ) : (
          <Alert tone="success">{t('shiftSwaps.review.noConflict')}</Alert>
        )
      ) : null}
      {review?.sameCrewWarning ? <Alert tone="warning">{t('shiftSwaps.review.sameCrew')}</Alert> : null}
      {error ? <Alert tone="error">{error}</Alert> : null}
      {onDecide && item.canDecide ? (
        <fieldset disabled={busy} className="space-y-3">
          {allowDecline ? (
            <TextArea
              label={t('shiftSwaps.decisionNote')}
              maxLength={1000}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          ) : null}
          <div className="flex flex-wrap gap-3">
            <Button
              variant="primary"
              busy={busy}
              disabled={!!review?.conflict}
              onClick={() => onDecide('approved', note)}
            >
              {t('shiftSwaps.approve')}
            </Button>
            {allowDecline ? (
              <Button onClick={() => onDecide('declined', note)}>{t('shiftSwaps.declineSwap')}</Button>
            ) : null}
          </div>
        </fieldset>
      ) : null}
    </article>
  );
}
